import React, { useState } from "react";
import styled from "styled-components";

const highlightJson = (value) => {
  const json = JSON.stringify(value, null, 2);
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let color = "#79c0ff";
      if (/^"/.test(match)) {
        color = /:$/.test(match) ? "#45fe47" : "#a5d6ff";
      } else if (/true|false/.test(match)) {
        color = "#ffa657";
      } else if (/null/.test(match)) {
        color = "#8b949e";
      }
      return `<span style="color:${color}">${match}</span>`;
    }
  );
};

export default function ArchetypeCollectibleCard({
  item,
  json,
  apiStatus,
  flipped,
  onFlip,
}) {
  const [copied, setCopied] = useState(false);
  const accent = json?.color || "#45fe47";

  const copyJson = async (e) => {
    e.stopPropagation();
    if (!json) return;
    await navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <CardSlot>
      <CardShell
        role="button"
        tabIndex={0}
        $flipped={flipped}
        $accent={accent}
        onClick={onFlip}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onFlip();
          }
        }}
        aria-pressed={flipped}
        aria-label={
          flipped
            ? `Flip ${item.name} card face up`
            : `Flip ${item.name} card to view API JSON`
        }
      >
        <CardInner $flipped={flipped}>
          <CardFace $side="front" $accent={accent}>
            <FoilEdge />
            <CardArt>
              <img src={item.src} alt={item.name} />
            </CardArt>
            <CardBanner>
              <CardName>{item.name}</CardName>
              <CardHint>tap to flip</CardHint>
            </CardBanner>
          </CardFace>

          <CardFace $side="back" $accent={accent}>
            <BackHeader>
              <CopyBtn type="button" onClick={copyJson} disabled={!json}>
                {copied ? "Copied" : "Copy"}
              </CopyBtn>
            </BackHeader>
            <BackBody
              onClick={(e) => {
                if (window.matchMedia("(min-width: 769px)").matches) {
                  e.stopPropagation();
                }
              }}
            >
              {apiStatus === "loading" && (
                <BackStatus>Loading live archetype profile…</BackStatus>
              )}
              {apiStatus === "error" && (
                <BackStatus>
                  Could not load Archetype API. Tap again later or check the
                  Academy endpoint.
                </BackStatus>
              )}
              {apiStatus === "ready" && json && (
                <JsonPre
                  dangerouslySetInnerHTML={{ __html: highlightJson(json) }}
                />
              )}
              {apiStatus === "ready" && !json && (
                <BackStatus>
                  No live API profile matched “{item.name}”.
                </BackStatus>
              )}
            </BackBody>
            <BackFooter>tap to flip back</BackFooter>
          </CardFace>
        </CardInner>
      </CardShell>
    </CardSlot>
  );
}

const CardSlot = styled.div`
  perspective: 1200px;
  width: 100%;
`;

const CardShell = styled.div`
  display: block;
  width: 100%;
  aspect-ratio: 2.5 / 3.5;
  cursor: pointer;
  transform-style: preserve-3d;

  &:focus-visible {
    outline: 2px solid ${({ $accent }) => $accent || "#45fe47"};
    outline-offset: 3px;
  }

  &:hover > div {
    transform: ${({ $flipped }) =>
      $flipped
        ? "rotateY(180deg) translateY(-4px)"
        : "rotateY(0deg) translateY(-4px)"};
  }
`;

const CardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.65s cubic-bezier(0.4, 0.2, 0.2, 1);
  transform: ${({ $flipped }) =>
    $flipped ? "rotateY(180deg)" : "rotateY(0deg)"};
`;

const CardFace = styled.div`
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${({ $accent }) => $accent || "rgba(69, 254, 71, 0.55)"};
  box-shadow:
    0 10px 24px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(0, 0, 0, 0.65),
    inset 0 0 0 2px rgba(255, 255, 255, 0.06);
  background: ${({ $side }) =>
    $side === "back"
      ? "linear-gradient(160deg, #06110a, #020403 55%, #0a160e)"
      : "linear-gradient(160deg, #121a14, #070b08)"};
  transform: ${({ $side }) => ($side === "back" ? "rotateY(180deg)" : "none")};
  display: flex;
  flex-direction: column;
`;

const FoilEdge = styled.span`
  pointer-events: none;
  position: absolute;
  inset: 5px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    transparent 35%,
    transparent 65%,
    rgba(69, 254, 71, 0.12)
  );
  z-index: 2;
`;

const CardArt = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0.55rem 0.45rem 0;
  min-height: 0;

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.5));
  }

  @media (max-width: 768px) {
    align-items: center;
    justify-content: center;
    padding: 0.55rem;
  }
`;

const CardBanner = styled.div`
  position: relative;
  z-index: 3;
  padding: 0.35rem 0.45rem 0.45rem;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.85));
  text-align: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CardName = styled.div`
  font-family: "Orbitron", sans-serif;
  font-size: clamp(0.55rem, 1.4vw, 0.72rem);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #45fe47;
`;

const CardHint = styled.div`
  margin-top: 0.1rem;
  font-family: "VT323", monospace;
  font-size: 0.8rem;
  color: rgba(157, 255, 159, 0.55);
`;

const BackHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.28rem 0.35rem;
  border-bottom: 1px solid rgba(69, 254, 71, 0.25);
  background: rgba(69, 254, 71, 0.06);

  @media (max-width: 768px) {
    display: none;
  }
`;

const CopyBtn = styled.button`
  flex-shrink: 0;
  appearance: none;
  font-family: "VT323", monospace;
  font-size: 0.65rem;
  padding: 0.1rem 0.3rem;
  color: #45fe47;
  background: transparent;
  border: 1px solid rgba(69, 254, 71, 0.55);
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background: rgba(69, 254, 71, 0.15);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const BackBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #45fe47;
    border-radius: 8px;
  }
`;

const JsonPre = styled.pre`
  margin: 0;
  padding: 0.3rem 0.35rem 0.5rem;
  font-family: "Cascadia Code", "Fira Code", "VT323", monospace;
  font-size: clamp(0.42rem, 0.85vw, 0.52rem);
  line-height: 1.25;
  color: #9dff9f;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
`;

const BackStatus = styled.p`
  margin: 0;
  padding: 0.55rem 0.4rem;
  font-family: "VT323", monospace;
  font-size: 0.7rem;
  color: #8b949e;
  text-align: left;
`;

const BackFooter = styled.div`
  padding: 0.15rem 0.3rem 0.25rem;
  border-top: 1px solid rgba(69, 254, 71, 0.2);
  font-family: "VT323", monospace;
  font-size: 0.62rem;
  color: rgba(157, 255, 159, 0.5);
  text-align: center;

  @media (max-width: 768px) {
    display: none;
  }
`;
